class InfiniteScroll extends HTMLElement {
    constructor() {
      super();
      this.currentPage = parseInt(this.dataset.currentPage) || 1;
      this.totalPages = parseInt(this.dataset.totalPages) || 1;
      this.isLoading = false;
      this.isEnabled = true;
    }
  
    connectedCallback() {
      // Wait for element to be fully rendered in DOM
      requestAnimationFrame(() => {
        this.productGrid = document.getElementById('product-grid');
        this.loadingIndicator = this.querySelector('.infinite-scroll-loading');
        this.endMessage = this.querySelector('.infinite-scroll-end');
        this.sentinel = this.querySelector('.infinite-scroll-sentinel');
  
        if (this.currentPage >= this.totalPages) {
          this.showEndMessage();
          return;
        }
  
        this.initIntersectionObserver();
        this.checkInitialPage();
      });
    }
  
    initIntersectionObserver() {
      // Use Intersection Observer for better performance and accuracy
      const options = {
        root: null,
        rootMargin: '600px', // Start loading 600px before the sentinel comes into view
        threshold: 0
      };
  
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isLoading && this.currentPage < this.totalPages && this.isEnabled) {
            this.loadNextPage();
          }
        });
      }, options);
  
      if (this.sentinel) {
        this.observer.observe(this.sentinel);
      }
    }
  
    checkInitialPage() {
      // Check if URL has a page parameter and load previous pages if needed
      const urlParams = new URLSearchParams(window.location.search);
      const urlPage = parseInt(urlParams.get('page'));
  
      if (urlPage && urlPage > 1 && urlPage <= this.totalPages) {
        // Clear ALL items in the grid since they're only from the URL page
        if (this.productGrid) {
          const existingItems = this.productGrid.querySelectorAll('.grid__item');
          existingItems.forEach(item => item.remove());
        }
  
        // Reset to page 1 and load all pages up to the URL page
        this.currentPage = 1;
        this.loadPagesUpTo(urlPage);
      }
    }
  
    async loadPagesUpTo(targetPage) {
      this.isEnabled = false;
      this.showLoading();
  
      // Start from page 1 if the grid was cleared
      const startPage = this.currentPage || 1;
  
      for (let page = startPage; page <= targetPage; page++) {
        await this.loadPage(page, false);
      }
  
      this.currentPage = targetPage;
      this.isEnabled = true;
      this.hideLoading();
  
      // Scroll to the appropriate position after loading
      setTimeout(() => {
        const hash = window.location.hash;
        if (hash === '#ProductGridContainer') {
          document.getElementById('ProductGridContainer')?.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  
    async loadNextPage() {
      if (this.isLoading || this.currentPage >= this.totalPages) return;
  
      this.isLoading = true;
  
      // Show loading immediately for better UX
      this.showLoading();
  
      // Small delay to ensure loading state is visible
      await new Promise(resolve => setTimeout(resolve, 100));
  
      const nextPage = this.currentPage + 1;
      await this.loadPage(nextPage);
    }
  
    async loadPage(pageNumber) {
      try {
        const url = this.buildPageUrl(pageNumber);
        const response = await fetch(url);
  
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
  
        const html = await response.text();
        const productsAdded = this.appendProducts(html, pageNumber === 1);
  
        if (productsAdded > 0) {
          this.currentPage = pageNumber;
  
          if (this.currentPage >= this.totalPages) {
            this.showEndMessage();
            this.disconnect();
          }
  
          // Trigger scroll animations if they exist
          if (typeof initializeScrollAnimationTrigger === 'function') {
            initializeScrollAnimationTrigger();
          }
        }
  
      } catch (error) {
        console.error('[Infinite Scroll] Error loading page:', error);
        this.showError();
      } finally {
        this.isLoading = false;
        this.hideLoading();
      }
    }
  
    buildPageUrl(pageNumber) {
      const currentUrl = new URL(window.location.href);
      const params = new URLSearchParams(currentUrl.search);
  
      // Set the page parameter
      params.set('page', pageNumber);
  
      // Get the section ID from the product grid
      const sectionId = this.productGrid?.dataset.id;
      if (sectionId) {
        params.set('section_id', sectionId);
      }
  
      return `${currentUrl.pathname}?${params.toString()}`;
    }
  
    appendProducts(html, isFirstPage = false) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
  
      // Check if we got the product grid
      const productGridInResponse = doc.querySelector('#product-grid');
      if (!productGridInResponse) {
        return 0;
      }
  
      const newProducts = productGridInResponse.querySelectorAll('.grid__item');
  
      if (newProducts.length === 0) {
        return 0;
      }
  
      const fragment = document.createDocumentFragment();
      let addedCount = 0;

      let currentProductCount = this.productGrid.querySelectorAll('.grid__item:not(.separator-image)').length;
      let hasFeedSeparator = this.productGrid.querySelector('.feed-separator') !== null;
  
      newProducts.forEach(item => {
        // For first page, include separators; for subsequent pages, skip them
        const isSeparator = item.classList.contains('separator-image');

        const isFeedSeparator = item.classList.contains('feed-separator');

  
        if (isFirstPage || !isSeparator) {
          fragment.appendChild(item.cloneNode(true));
          addedCount++;
        } else if (!isSeparator || isFeedSeparator) {
          // For feed separators, only add if we don't already have one
          if (isFeedSeparator) {
            if (!hasFeedSeparator) {
              fragment.appendChild(item.cloneNode(true));
              addedCount++;
              hasFeedSeparator = true;
            }
          } else {
            fragment.appendChild(item.cloneNode(true));
            addedCount++;
            currentProductCount++;
          }
        }
      });
  
      if (addedCount > 0) {
        this.productGrid.appendChild(fragment);
      }
  
      return addedCount;
    }
  
    showLoading() {
      if (this.loadingIndicator) {
        this.loadingIndicator.classList.remove('hidden');
      }
    }
  
    hideLoading() {
      if (this.loadingIndicator) {
        this.loadingIndicator.classList.add('hidden');
      }
    }
  
    showEndMessage() {
      if (this.endMessage) {
        this.endMessage.classList.remove('hidden');
      }
    }
  
    showError() {
      // You can customize this to show a better error message
      console.error('Failed to load more products');
    }
  
    reset() {
      // Reset to first page when filters change
      this.currentPage = 1;
      this.isLoading = false;
      this.isEnabled = true;
  
      // Update total pages from the new data
      const newTotalPages = parseInt(this.dataset.totalPages);
      if (newTotalPages) {
        this.totalPages = newTotalPages;
      }
  
      this.hideLoading();
  
      if (this.endMessage) {
        this.endMessage.classList.add('hidden');
      }
  
      // Re-initialize observer if needed
      if (this.currentPage < this.totalPages && this.sentinel && this.observer) {
        this.observer.observe(this.sentinel);
      }
    }
  
    disconnect() {
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  
    disconnectedCallback() {
      this.disconnect();
    }
  }
  
  customElements.define('infinite-scroll', InfiniteScroll);
  
  // Handle browser back/forward buttons
  window.addEventListener('popstate', (event) => {
    // Let the facets.js handle the page reload
    // The infinite scroll will reinitialize after the page content is updated
  });